import {
  TicketHistoryItem,
  useTicketActivityParser,
} from "@jugl-web/domain-resources/tickets";
import { useTicketLogs } from "@jugl-web/domain-resources/tickets/hooks/useTicketLogs";
import { TicketDetailsModel } from "@jugl-web/rest-api/tickets/models/TicketDetails";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useMemo, useRef } from "react";
import { VList, VListHandle } from "virtua";

export type HistoryTabContentProps = {
  ticket: TicketDetailsModel;
};

export const HistoryTabContent: FC<HistoryTabContentProps> = ({ ticket }) => {
  const { entityId } = useEntitySelectedProvider();
  const vListRef = useRef<VListHandle>(null);
  const { parseTicketLogs } = useTicketActivityParser();

  const {
    data: logs,
    loadMore,
    isError,
    hasMore,
    isLoadingMore,
    isLoading,
    retry,
    isLoadingMoreError,
  } = useTicketLogs({
    entityId,
    ticketId: ticket.id,
    pageSize: 20,
    skip: !entityId,
  });

  const activities = useMemo(
    () => parseTicketLogs(logs),
    [logs, parseTicketLogs]
  );

  const handleVListOnScroll = useCallback(
    (offset: number) => {
      if (!vListRef.current) {
        return;
      }
      if (!hasMore || isLoadingMore) {
        return;
      }
      const bottomOffset =
        offset - vListRef.current.scrollSize + vListRef.current.viewportSize;

      if (bottomOffset > -150) {
        loadMore();
      }
    },
    [hasMore, isLoadingMore, loadMore]
  );

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
      className="h-full min-h-0"
    >
      {activities.length > 0 && (
        <VList
          ref={vListRef}
          overscan={5}
          onScroll={handleVListOnScroll}
          className="jugl__custom-scrollbar h-full p-8"
        >
          {activities.map((activity, index) => (
            <TicketHistoryItem
              key={activity.id}
              activity={activity}
              isLast={index === activities.length - 1}
            />
          ))}
        </VList>
      )}
    </DataLoadingWrapper>
  );
};
